import { CommonDateFunctions } from './CommonDateFunctions';

function getStartOfWeek(date = new Date()) {
  const startDate = new Date();

  startDate.setDate(date.getDate() - (date.getDay() || 7) + 1);

  return startDate;
}

function getEndOfWeek(date = new Date()) {
  const endDate = new Date();

  endDate.setDate(date.getDate() - (date.getDay() || 7) + 7);

  return endDate;
}

export function getWeekRange(date = new Date()) {
  return [getStartOfWeek(date), getEndOfWeek(date)];
}

export function getDateOffsetFromCurrent(dateTime: Date): number {
  if (dateTime) {
    const msInDay = 24 * 60 * 60 * 1000;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const date = new Date(
      dateTime.getFullYear(),
      dateTime.getMonth(),
      dateTime.getDate()
    );
    return Math.round((Number(date) - Number(today)) / msInDay);
  }
  return null;
}

/** Helper to convert any ISO or Date to yyyy-MM-dd
 * It converts a Date or date‐string into a YYYY-MM-DD string suitable for <input type="date"> or kendo.*/
export function toDateInput(
  val: string | Date | null,
  keepAsUtc = false
): string {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  // ensure correct timezone adjustment
  // JavaScript Date.toISOString() always output UTC dates.
  // Subtracting the timezone offset produces a date whose ISO string will match the local date.
  const local = new Date(
    d.getTime() - (keepAsUtc ? 0 : d.getTimezoneOffset() * 60000)
  );
  // ISO (YYYY-MM-DDTHH:mm:ss.sssZ) => "YYYY-MM-DD"
  return local.toISOString().split('T')[0];
}

/**
 * Parses a given value into a valid JavaScript Date object.
 *
 * The function handles multiple input types:
 * 1. If the value is `null`, `undefined`, or otherwise falsy, it returns `null`.
 * 2. If the value is already a `Date` object, it returns the same date if valid,
 *    otherwise `null` if the date is invalid.
 * 3. If the value is a string in `YYYY-MM-DD` format, it parses it as a local date
 *    (taking month indexing into account) and returns a Date, or `null` if invalid.
 * 4. For all other strings or values, it falls back to `new Date(value)` and returns
 *    a valid Date or `null` if parsing fails.
 *
 * This ensures that the returned value is always either a valid Date object or null.
 *
 * @param value - The value to parse, can be a Date, string, or any unknown type.
 * @returns A valid Date object if parsing succeeds, otherwise `null`.
 */
export function parseLocalDate(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const str = String(value);
  // Expecting yyyy-MM-dd from date input
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (match) {
    const [, y, m, d] = match;
    const localDate = new Date(Number(y), Number(m) - 1, Number(d)); // local time, no TZ shift
    if (
      isNaN(localDate.getTime()) ||
      localDate.getFullYear() !== Number(y) ||
      localDate.getMonth() !== Number(m) - 1 ||
      localDate.getDate() !== Number(d)
    ) {
      return null;
    }
    return localDate;
  }

  const fallbackDate = new Date(str);
  return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
}

/**
 * Best-effort `Date` for JSON/API values (`Date`, ISO string, etc.).
 * Invalid or missing values return `null`.
 */
export function coerceToDate(value: unknown): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  return parseLocalDate(value);
}

/**
 * Milliseconds since epoch for JSON/API values (`Date`, ISO string, etc.).
 * Missing or invalid values return `NaN`.
 */
export function dateTimeMsFromUnknown(value: unknown): number {
  const d = coerceToDate(value);
  return d ? d.getTime() : NaN;
}

/** Formats a Date as local calendar date in YYYY-MM-DD (no timezone conversion). */
export function formatDateAsIsoLocal(date: Date): string {
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Two-digit pad (legacy json-date-ex `dateExPad`).
 */
function padDateTimeComponent(num: number): string {
  return num < 10 ? `0${num}` : String(num);
}

/**
 * Local wall-clock `YYYY-MM-DDTHH:mm:ss.sss` with **no** `Z` or offset (legacy
 * json-date-ex mode 2). Uses **`get*` / local** getters.
 *
 * Use for **in-memory** `JSON.stringify` → `JSON.parse` round-trips (`parseData`,
 * {@link stringifyWithLocalCalendarDates}) so `Date` values built from local
 * pickers / local components do not shift when re-parsed.
 */
export function formatDateTimeLocalWireUnspecifiedTz(
  date: Date
): string | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const y = date.getFullYear().toString().padStart(4, '0');
  const mo = padDateTimeComponent(date.getMonth() + 1);
  const day = padDateTimeComponent(date.getDate());
  const h = padDateTimeComponent(date.getHours());
  const mi = padDateTimeComponent(date.getMinutes());
  const s = padDateTimeComponent(date.getSeconds());
  const msPart = (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5);
  return `${y}-${mo}-${day}T${h}:${mi}:${s}.${msPart}`;
}

/**
 * Same shape as {@link formatDateTimeLocalWireUnspecifiedTz} but uses **`getUTC*`**.
 *
 * Use for **HTTP POST/PUT/PATCH** bodies when `Date` values are real UTC instants
 * (e.g. revived from `…Z`). Avoids +1h in UTC+1 that local getters would add for
 * UTC-midnight calendar fields.
 */
export function formatDateTimeUtcWireUnspecifiedTz(date: Date): string | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const y = date.getUTCFullYear().toString().padStart(4, '0');
  const mo = padDateTimeComponent(date.getUTCMonth() + 1);
  const day = padDateTimeComponent(date.getUTCDate());
  const h = padDateTimeComponent(date.getUTCHours());
  const mi = padDateTimeComponent(date.getUTCMinutes());
  const s = padDateTimeComponent(date.getUTCSeconds());
  const msPart = (date.getUTCMilliseconds() / 1000).toFixed(3).slice(2, 5);
  return `${y}-${mo}-${day}T${h}:${mi}:${s}.${msPart}`;
}

/**
 * Deep-clone and replace every `Date` with {@link formatDateTimeLocalWireUnspecifiedTz}.
 */
export function normalizeDatesToLocalWireStringsDeep<T>(value: T): T {
  if (value == null) {
    return value;
  }
  if (value instanceof Date) {
    const wired = formatDateTimeLocalWireUnspecifiedTz(value);
    return (wired === null ? value : wired) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeDatesToLocalWireStringsDeep(item)) as T;
  }
  if (typeof value !== 'object') {
    return value;
  }

  const output = Object.create(
    Object.getPrototypeOf(value) ?? Object.prototype
  );
  for (const [key, nestedValue] of Object.entries(value)) {
    output[key] = normalizeDatesToLocalWireStringsDeep(nestedValue);
  }
  return output;
}

/**
 * Deep-clone and replace every `Date` with {@link formatDateTimeUtcWireUnspecifiedTz}.
 */
export function normalizeDatesToUtcWireStringsDeep<T>(value: T): T {
  if (value == null) {
    return value;
  }
  if (value instanceof Date) {
    const wired = formatDateTimeUtcWireUnspecifiedTz(value);
    return (wired === null ? value : wired) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeDatesToUtcWireStringsDeep(item)) as T;
  }
  if (typeof value !== 'object') {
    return value;
  }

  const output = Object.create(
    Object.getPrototypeOf(value) ?? Object.prototype
  );
  for (const [key, nestedValue] of Object.entries(value)) {
    output[key] = normalizeDatesToUtcWireStringsDeep(nestedValue);
  }
  return output;
}

/**
 * `JSON.stringify` after rewriting `Date` values to local wire strings (mode 2),
 * so payloads do not use `Date.prototype.toJSON` / UTC `Z`.
 */
export function stringifyWithLocalCalendarDates(
  value: unknown,
  space?: string | number
): string {
  return JSON.stringify(
    normalizeDatesToLocalWireStringsDeep(value),
    null,
    space
  );
}

/**
 * Calendar date as UTC midnight for the user's **local** calendar day (year/month/date).
 * Produces a stable **instant** whose UTC Y/M/D match that local calendar day.
 *
 * Prefer {@link toLocalMidnightForCalendarDate} for values sent on **POST/PUT/PATCH** through
 * `MyHttpClientFactory` — `HttpLocalCalendarDateBodyInterceptor` serializes with **local**
 * getters, so a UTC-midnight `Date` becomes a non-midnight wire time in non-UTC zones
 * (e.g. `T02:00:00` in UTC+2 instead of `T00:00:00`).
 *
 * Still use this for {@link localCalendarUtcMidnightsInRange} and other logic that needs a
 * single instant per calendar day independent of local clock display.
 *
 * Accepts `Date`, ISO string, or null/undefined. Invalid values return `null`.
 */
export function toUtcMidnightForLocalCalendarDate(
  value: Date | string | null | undefined
): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

/**
 * Inclusive calendar-day count between two dates, counting both endpoints
 * (`end - start + 1`). Each side is reduced to a UTC midnight built from its
 * **local** calendar components ({@link toUtcMidnightForLocalCalendarDate}), so
 * the span is immune to daylight-saving transitions: a 23-hour local day would
 * otherwise floor one day short via elapsed-ms division (`WI 29283` review).
 *
 * Accepts `Date`, ISO/`yyyy-MM-dd` string, or null/undefined. Returns `null`
 * when either value is missing/unreadable or `end` falls before `start`; callers
 * that need an `endBeforeStart` distinction guard that case before calling.
 */
export function inclusiveCalendarDayCount(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): number | null {
  const startUtc = toUtcMidnightForLocalCalendarDate(start);
  const endUtc = toUtcMidnightForLocalCalendarDate(end);
  if (!startUtc || !endUtc) return null;
  if (endUtc.getTime() < startUtc.getTime()) return null;
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((endUtc.getTime() - startUtc.getTime()) / msPerDay) + 1;
}

/**
 * **Local** midnight for the calendar day given by the value's **local** `getFullYear` /
 * `getMonth` / `getDate` (after parsing if string).
 *
 * Use before POST/PUT/PATCH bodies for **date-only** fields so
 * `HttpLocalCalendarDateBodyInterceptor` emits `YYYY-MM-DDT00:00:00.000` on the wire.
 * Matches grid columns built with `new Date(y, m, d)` (e.g. Equipment Usage).
 */
export function toLocalMidnightForCalendarDate(
  value: Date | string | null | undefined
): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

export function sameLocalCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Same local calendar day after coercing both sides (`Date`, ISO string from JSON, etc.).
 * Two missing/invalid values are treated as equal (both empty).
 */
export function sameLocalCalendarDayCoerced(a: unknown, b: unknown): boolean {
  const da = parseLocalDate(a);
  const db = parseLocalDate(b);
  if (!da && !db) {
    return true;
  }
  if (!da || !db) {
    return false;
  }
  return sameLocalCalendarDay(da, db);
}

/**
 * Sparta / .NET often serializes `DateTime` as `YYYY-MM-DDTHH:mm:ss[.fff]` with **no** `Z` or offset.
 * For grid matching, treat the **date portion** as the calendar day — do not rely on `Date` parsing
 * (spec = local midnight; server intent is usually “this work day” regardless of client TZ).
 *
 * Returns `null` if the string has `Z` or a numeric offset — those use normal {@link coerceToDate} logic.
 */
function calendarDayKeyFromUnspecifiedWireDateTime(
  value: string
): string | null {
  const wire = value.trim();
  const m = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.exec(wire);
  return m ? m[1] : null;
}

/**
 * True when wall time in UTC is midnight (00:00:00, any ms). Typical API “calendar day” with `Z`.
 * Sub-second noise from serialization still counts so we don’t fall through to local keys and
 * double-match columns (Americas + Tokyo cases).
 */
function isUtcMidnightCalendarInstant(date: Date): boolean {
  return (
    !Number.isNaN(date.getTime()) &&
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0
  );
}

/**
 * Single `YYYY-MM-DD` key for `DateUsed` when matching equipment grid columns.
 * - UTC-midnight instants (…Z date-only from API) → **UTC** calendar day (not local), so one column only.
 * - Everything else → {@link formatDateAsIsoLocal} (in-session local midnight, no-Z local parses, etc.).
 */
function calendarDayKeyFromDateUsedForGridMatch(date: Date): string {
  if (isUtcMidnightCalendarInstant(date)) {
    const y = date.getUTCFullYear().toString().padStart(4, '0');
    const m = padDateTimeComponent(date.getUTCMonth() + 1);
    const d = padDateTimeComponent(date.getUTCDate());
    return `${y}-${m}-${d}`;
  }
  return formatDateAsIsoLocal(date);
}

/**
 * Grid column is a local calendar day; `DateUsed` may be local (in-session) or UTC-midnight from API.
 * Compare one **string calendar key** per side so a detail cannot match two columns (OR logic bug).
 *
 * `dateUsed` may be a `Date` or an ISO string from JSON (e.g. `2026-03-23T00:00:00` or `...Z`).
 */
export function sameCalendarDayColumnAndDateUsed(
  columnDate: Date,
  dateUsed: Date | string | null | undefined
): boolean {
  if (dateUsed == null || dateUsed === '') {
    return false;
  }
  if (typeof dateUsed === 'string') {
    const wireKey = calendarDayKeyFromUnspecifiedWireDateTime(dateUsed);
    if (wireKey !== null) {
      return formatDateAsIsoLocal(columnDate) === wireKey;
    }
  }
  const dUsed = coerceToDate(dateUsed);
  if (dUsed == null) {
    return false;
  }
  return (
    formatDateAsIsoLocal(columnDate) ===
    calendarDayKeyFromDateUsedForGridMatch(dUsed)
  );
}

/**
 * Two labor/work instants are the same calendar day (local column date vs API UTC midnight, etc.).
 * Accepts JSON date strings or `Date` instances.
 */
export function sameCalendarDayLaborDates(a: unknown, b: unknown): boolean {
  const da = coerceToDate(a);
  const db = coerceToDate(b);
  if (!da || !db) return false;
  if (sameLocalCalendarDay(da, db)) return true;
  if (
    da.getUTCFullYear() === db.getUTCFullYear() &&
    da.getUTCMonth() === db.getUTCMonth() &&
    da.getUTCDate() === db.getUTCDate()
  ) {
    return true;
  }
  return (
    sameCalendarDayColumnAndDateUsed(da, db) ||
    sameCalendarDayColumnAndDateUsed(db, da)
  );
}

/**
 * Each calendar day from rangeStart through rangeEnd (inclusive), max `maxDays` entries.
 * Uses local noon when stepping days to avoid DST edge cases.
 */
export function localCalendarUtcMidnightsInRange(
  rangeStart: Date,
  rangeEnd: Date,
  maxDays: number
): Date[] {
  const out: Date[] = [];
  const cursor = new Date(
    rangeStart.getFullYear(),
    rangeStart.getMonth(),
    rangeStart.getDate(),
    12,
    0,
    0
  );
  const endAnchor = new Date(
    rangeEnd.getFullYear(),
    rangeEnd.getMonth(),
    rangeEnd.getDate(),
    12,
    0,
    0
  );
  while (cursor.getTime() <= endAnchor.getTime() && out.length < maxDays) {
    const utcMidnight = toUtcMidnightForLocalCalendarDate(cursor);
    if (utcMidnight !== null) {
      out.push(utcMidnight);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  if (out.length) {
    return out;
  }

  const fallback = toUtcMidnightForLocalCalendarDate(new Date());
  return fallback === null ? [] : [fallback];
}

/**
 * Renders a DTO date field to the wire the way `CommonDateFunctions.ToDateString`
 * does for a `Date` (mm/dd/yyyy), but tolerates a value the
 * `HttpLocalCalendarDateBodyInterceptor` already rewrote from a `Date` into a wire
 * string: it passes such a string straight through instead of calling `Date`
 * methods on it. Calling those on the already-serialized string threw inside
 * `JSON.stringify` and silently dropped the per-diem createSpartaSideRecord POST
 * (no network call, no saved doc). Anything else becomes null.
 */
export function toWireDateString(value: unknown): string | null {
  if (value instanceof Date) {
    return CommonDateFunctions.ToDateString(value);
  }
  return typeof value === 'string' ? value : null;
}
